/**
 * IndexedDB-backed persistence for in-progress voice recordings (PRD-941, Story 1).
 *
 * MediaRecorder emits `dataavailable` chunks on a timeslice while recording. We persist
 * each chunk here as it arrives, so a tab close / crash mid-recording leaves a
 * reconstructable blob the user can resume as a draft on return — instead of losing it.
 *
 * One object store keyed by an auto-incrementing id; each record is
 * { draftId, seq, blob } for chunks and a separate meta record per draft.
 */

const DB_NAME = 'resai_voice_drafts';
const DB_VERSION = 1;
const CHUNK_STORE = 'chunks';
const META_STORE = 'meta';

// Drafts older than this are pruned on load — a stale recording from weeks ago is noise.
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        const store = db.createObjectStore(CHUNK_STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('draftId', 'draftId', { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'draftId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open voice draft DB'));
  });
}

function tx(db, storeNames, mode) {
  const transaction = db.transaction(storeNames, mode);
  return transaction;
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Create/overwrite a draft's metadata record. */
export async function saveDraftMeta(draftId, meta) {
  try {
    const db = await openDB();
    const t = tx(db, [META_STORE], 'readwrite');
    t.objectStore(META_STORE).put({ draftId, updatedAt: Date.now(), ...meta });
    await new Promise((res, rej) => {
      t.oncomplete = res;
      t.onerror = () => rej(t.error);
    });
    db.close();
  } catch (e) {
    // Persistence is best-effort — never let a draft write break an active recording.
    console.warn('[voiceDraftStore] saveDraftMeta failed:', e);
  }
}

/** Append one recorded chunk for a draft. Best-effort; logs and continues on failure. */
export async function appendChunk(draftId, seq, blob) {
  try {
    const db = await openDB();
    const t = tx(db, [CHUNK_STORE], 'readwrite');
    t.objectStore(CHUNK_STORE).add({ draftId, seq, blob });
    await new Promise((res, rej) => {
      t.oncomplete = res;
      t.onerror = () => rej(t.error);
    });
    db.close();
  } catch (e) {
    console.warn('[voiceDraftStore] appendChunk failed:', e);
  }
}

/** Delete a draft's chunks and meta (after successful submit or explicit discard). */
export async function deleteDraft(draftId) {
  try {
    const db = await openDB();
    const t = tx(db, [CHUNK_STORE, META_STORE], 'readwrite');
    const index = t.objectStore(CHUNK_STORE).index('draftId');
    const keysReq = index.getAllKeys(IDBKeyRange.only(draftId));
    keysReq.onsuccess = () => {
      (keysReq.result || []).forEach((key) => t.objectStore(CHUNK_STORE).delete(key));
    };
    t.objectStore(META_STORE).delete(draftId);
    await new Promise((res, rej) => {
      t.oncomplete = res;
      t.onerror = () => rej(t.error);
    });
    db.close();
  } catch (e) {
    console.warn('[voiceDraftStore] deleteDraft failed:', e);
  }
}

/**
 * Return the most-recent recoverable draft, pruning expired ones. Reassembles the
 * persisted chunks into a single Blob using the recorded mime type.
 * @returns {Promise<{draftId, name, mimeType, durationSeconds, recordedAt, blob}|null>}
 */
export async function getLatestDraft() {
  try {
    const db = await openDB();
    const metas = await reqToPromise(tx(db, [META_STORE], 'readonly').objectStore(META_STORE).getAll());
    const now = Date.now();
    const fresh = (metas || []).filter((m) => now - (m.updatedAt || 0) <= DRAFT_TTL_MS);
    const stale = (metas || []).filter((m) => now - (m.updatedAt || 0) > DRAFT_TTL_MS);
    db.close();

    // Prune stale drafts so they don't accumulate.
    await Promise.all(stale.map((m) => deleteDraft(m.draftId)));

    if (fresh.length === 0) return null;
    fresh.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const meta = fresh[0];

    const db2 = await openDB();
    const index = tx(db2, [CHUNK_STORE], 'readonly').objectStore(CHUNK_STORE).index('draftId');
    const chunks = await reqToPromise(index.getAll(IDBKeyRange.only(meta.draftId)));
    db2.close();

    if (!chunks || chunks.length === 0) return null;
    chunks.sort((a, b) => a.seq - b.seq);
    const blob = new Blob(chunks.map((c) => c.blob), { type: meta.mimeType || 'audio/webm' });
    return {
      draftId: meta.draftId,
      name: meta.name || '',
      mimeType: meta.mimeType || 'audio/webm',
      durationSeconds: meta.durationSeconds || 0,
      recordedAt: meta.recordedAt || null,
      blob,
    };
  } catch (e) {
    console.warn('[voiceDraftStore] getLatestDraft failed:', e);
    return null;
  }
}
